/**
 * AutoRenderedApprovalCard — Sprint 2 schema-driven approval renderer.
 *
 * This is the default approval card UI. It reads
 * `approval.content_schema_jsonb`, validates the shape with the canonical
 * Zod schema, and lays out:
 *   - editable fields  → <EditableField /> + (if aiEditable) <AIEditBox />
 *   - context panels   → <ContextPanel />
 *   - actions          → <ActionBar />
 *
 * Hand-designed exception renderers (Sprint 10+: email_draft, purchase_order,
 * etc.) are dispatched via the `renderers` prop — pass a map keyed by
 * displayType to override the auto-layout. This PR ships ONE hand-designed
 * renderer (`email_draft`) plus a minimal Sprint-2 renderer for the
 * `customer_match` displayType added in v1.1 Q6.
 *
 * Authority: docs/STATEFUL-WORKFLOW-PLATFORM-SPEC.md §8.4 + §8.5.
 *
 * Defense-in-depth:
 *   - If content_schema_jsonb is null/undefined → caller should fall back
 *     to the legacy generic JSON viewer; we render `null` (the caller
 *     gates on schema presence).
 *   - If parsing FAILS → render an in-card error banner + raw JSON of the
 *     approval evidence so the user still sees something actionable.
 */

import * as React from 'react';
import { AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  ApprovalContentSchemaSchema,
  readPath,
  writePath,
  type ApprovalContentSchema,
  type FieldDef,
  type Action,
  type DisplayType,
} from '@argusai/shared';
import { EditableField } from './EditableField';
import { AIEditBox } from './AIEditBox';
import { ContextPanel } from './ContextPanel';
import { ActionBar } from './ActionBar';
import { EmailDraftRenderer } from './renderers/EmailDraftRenderer';
import { CustomerMatchRenderer } from './renderers/CustomerMatchRenderer';
import { PurchaseOrderRenderer } from './renderers/PurchaseOrderRenderer';
import { CrmResponseRenderer } from './renderers/CrmResponseRenderer';
import { AccessChangeRenderer } from './renderers/AccessChangeRenderer';
import { DocumentReviewRenderer } from './renderers/DocumentReviewRenderer';

export interface AutoRenderedApprovalProps {
  /**
   * Approval row passed through unchanged. We only read:
   *   - id (for AI-edit endpoint dispatch)
   *   - evidence (read-only baseline)
   *   - decidedEvidence (user / AI edits applied on top of evidence)
   *   - contentSchemaJsonb (the schema declaration)
   *
   * Untyped here because callers integrate with their own approval-row VM.
   */
  approval: {
    id: string;
    evidence?: Record<string, unknown> | null;
    decidedEvidence?: Record<string, unknown> | null;
    contentSchemaJsonb?: unknown;
  };
  /**
   * Called when the user fires an action from the ActionBar. The caller
   * wires this to its approve / reject / edit-approve mutations.
   *
   * `editedEvidence` is the live in-memory copy with all field edits
   * applied — the caller persists it via `/edit-approve` (or whatever
   * Sprint 9 lands).
   */
  onDecide: (args: {
    action: Action;
    editedEvidence: Record<string, unknown>;
  }) => void | Promise<void>;
  /**
   * Optional renderer overrides keyed by displayType. Hand-designed
   * renderers (email_draft, purchase_order, etc.) plug in here. Sprint 2
   * pre-registers EmailDraftRenderer + CustomerMatchRenderer.
   */
  renderers?: Partial<Record<DisplayType, RendererComponent>>;
  /** Disables all editing (e.g. once a decision is in flight). */
  disabled?: boolean;
  className?: string;
}

export interface RendererComponent {
  (props: {
    approval: AutoRenderedApprovalProps['approval'];
    schema: ApprovalContentSchema;
    evidence: Record<string, unknown>;
    onFieldChange: (path: string, newValue: unknown) => void;
    onDecide: (action: Action) => void | Promise<void>;
    disabled?: boolean;
  }): React.ReactElement;
}

// Default renderer registry — extensible per-call via the `renderers` prop.
//
// Sprint 12: register the four additional hand-designed renderers
// (purchase_order, crm_response, access_change, document_review) per spec
// §8.5. document_review was deferred during the original Sprint 12 build
// and is now wired by FIX2-FRONTEND.
const DEFAULT_RENDERERS: Partial<Record<DisplayType, RendererComponent>> = {
  email_draft: EmailDraftRenderer,
  customer_match: CustomerMatchRenderer,
  purchase_order: PurchaseOrderRenderer,
  crm_response: CrmResponseRenderer,
  access_change: AccessChangeRenderer,
  document_review: DocumentReviewRenderer,
};

export function AutoRenderedApprovalCard({
  approval,
  onDecide,
  renderers,
  disabled = false,
  className,
}: AutoRenderedApprovalProps) {
  // Merge defaults + caller overrides so callers can OPT OUT of the V1
  // hand-designed email_draft by passing { email_draft: undefined } (or
  // their own replacement). Caller renderers win.
  const mergedRenderers: Partial<Record<DisplayType, RendererComponent>> = {
    ...DEFAULT_RENDERERS,
    ...(renderers ?? {}),
  };

  // Parse the schema once per (approval id × schema identity) — re-runs
  // when the row updates so an AI-edit response that rewrites the schema
  // is picked up.
  const parseResult = React.useMemo(() => {
    return ApprovalContentSchemaSchema.safeParse(approval.contentSchemaJsonb);
  }, [approval.contentSchemaJsonb]);

  // Working copy of evidence — starts from decided_evidence ?? evidence and
  // accumulates edits in memory until the user fires a decision.
  const baseEvidence = React.useMemo<Record<string, unknown>>(() => {
    const decided =
      approval.decidedEvidence && typeof approval.decidedEvidence === 'object'
        ? (approval.decidedEvidence as Record<string, unknown>)
        : null;
    const base =
      approval.evidence && typeof approval.evidence === 'object'
        ? (approval.evidence as Record<string, unknown>)
        : {};
    return decided ?? { ...base };
  }, [approval.evidence, approval.decidedEvidence]);

  const [editedEvidence, setEditedEvidence] = React.useState<
    Record<string, unknown>
  >(baseEvidence);

  // Reset edits if the underlying baseline changes (e.g. row refetched).
  React.useEffect(() => {
    setEditedEvidence(baseEvidence);
  }, [baseEvidence]);

  const onFieldChange = React.useCallback(
    (path: string, newValue: unknown) => {
      setEditedEvidence((prev) => writePath(prev, path, newValue));
    },
    [],
  );

  const handleDecide = React.useCallback(
    async (action: Action) => {
      await onDecide({ action, editedEvidence });
    },
    [editedEvidence, onDecide],
  );

  if (!parseResult.success) {
    return (
      <div
        className={cn(
          'rounded-lg border border-destructive/40 bg-destructive/5 p-4',
          className,
        )}
        data-auto-render="schema-invalid"
      >
        <div className="flex items-center gap-2 text-sm font-semibold text-destructive">
          <AlertTriangle className="h-4 w-4" />
          Content schema is invalid — falling back to JSON viewer
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          The workflow that produced this approval declared a malformed
          content schema. The approve / reject buttons remain functional via
          the standard approval UI; only the auto-rendered card is degraded.
        </p>
        <pre className="mt-3 max-h-96 overflow-auto whitespace-pre-wrap break-words rounded bg-background/60 p-2 font-mono text-[10px]">
          {JSON.stringify(baseEvidence, null, 2)}
        </pre>
      </div>
    );
  }

  const schema = parseResult.data;

  // Hand-designed renderer dispatch.
  const renderer = mergedRenderers[schema.displayType];
  if (renderer && schema.displayType !== 'generic') {
    return (
      <div
        className={cn('overflow-hidden rounded-lg border bg-card', className)}
        data-auto-render="hand-designed"
        data-display-type={schema.displayType}
      >
        {renderer({
          approval,
          schema,
          evidence: editedEvidence,
          onFieldChange,
          onDecide: handleDecide,
          disabled,
        })}
      </div>
    );
  }

  // Auto-render path — 2-column layout: editable fields | context panels.
  return (
    <div
      className={cn(
        'overflow-hidden rounded-lg border bg-card',
        className,
      )}
      data-auto-render="generic"
      data-display-type={schema.displayType}
    >
      <div className="grid gap-6 p-4 lg:grid-cols-[2fr,1fr]">
        {/* LEFT — editable fields */}
        <div className="space-y-4">
          {schema.editableFields.map((field) => (
            <FieldRow
              key={field.path}
              approvalId={approval.id}
              field={field}
              value={readPath(editedEvidence, field.path)}
              onChange={(v) => onFieldChange(field.path, v)}
              disabled={disabled}
            />
          ))}
          {schema.editableFields.length === 0 && (
            <p className="text-sm text-muted-foreground italic">
              No editable fields declared
            </p>
          )}
        </div>

        {/* RIGHT — context panels */}
        <aside className="space-y-3">
          {schema.contextPanels.map((panel) => (
            <ContextPanel
              key={panel.panelId}
              panel={panel}
              evidence={editedEvidence}
            />
          ))}
        </aside>
      </div>

      <ActionBar
        actions={schema.actions}
        approvalId={approval.id}
        onDecide={handleDecide}
        disabled={disabled}
      />
    </div>
  );
}

/**
 * Field row = EditableField + (if aiEditable) AIEditBox. Pulled out so the
 * hand-designed renderers can re-use it without duplicating the wiring.
 */
export function FieldRow({
  approvalId,
  field,
  value,
  onChange,
  disabled,
  className,
}: {
  approvalId: string;
  field: FieldDef;
  value: unknown;
  onChange: (next: unknown) => void;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <EditableField
        field={field}
        value={value}
        onChange={onChange}
        readOnly={disabled}
      />
      {field.aiEditable && (
        <AIEditBox
          field={field}
          approvalId={approvalId}
          onEdited={onChange}
        />
      )}
    </div>
  );
}
